export type DiscordPermissionFlag = {
    key: string;
    bit: bigint;
    privileged?: boolean;
    channelTypes?: readonly string[];
};

export type DiscordPermissionChannelType = {
    key: "T" | "V" | "S";
    label: string;
    channelTypes: readonly string[];
};

export type PermissionDiffEntry = DiscordPermissionFlag & {
    state: "added" | "removed";
};

export const DiscordPermissionChannelTypes = [
    {
        key          : "T",
        label        : "Text",
        channelTypes : [ "GUILD_TEXT", "GUILD_ANNOUNCEMENT", "GUILD_FORUM", "GUILD_MEDIA" ],
    },
    {
        key          : "V",
        label        : "Voice",
        channelTypes : [ "GUILD_VOICE" ],
    },
    {
        key          : "S",
        label        : "Stage",
        channelTypes : [ "GUILD_STAGE_VOICE" ],
    },
] as const satisfies readonly DiscordPermissionChannelType[];

export const DiscordPermissionFlags = [
    { key: "CREATE_INSTANT_INVITE", bit: 1n << 0n, channelTypes: [ "T", "V", "S" ] },
    { key: "KICK_MEMBERS", bit: 1n << 1n, privileged: true },
    { key: "BAN_MEMBERS", bit: 1n << 2n, privileged: true },
    { key: "ADMINISTRATOR", bit: 1n << 3n, privileged: true },
    { key: "MANAGE_CHANNELS", bit: 1n << 4n, privileged: true, channelTypes: [ "T", "V", "S" ] },
    { key: "MANAGE_GUILD", bit: 1n << 5n, privileged: true },
    { key: "ADD_REACTIONS", bit: 1n << 6n, channelTypes: [ "T", "V", "S" ] },
    { key: "VIEW_AUDIT_LOG", bit: 1n << 7n },
    { key: "PRIORITY_SPEAKER", bit: 1n << 8n, channelTypes: [ "V" ] },
    { key: "STREAM", bit: 1n << 9n, channelTypes: [ "V", "S" ] },
    { key: "VIEW_CHANNEL", bit: 1n << 10n, channelTypes: [ "T", "V", "S" ] },
    { key: "SEND_MESSAGES", bit: 1n << 11n, channelTypes: [ "T", "V", "S" ] },
    { key: "SEND_TTS_MESSAGES", bit: 1n << 12n, channelTypes: [ "T", "V", "S" ] },
    { key: "MANAGE_MESSAGES", bit: 1n << 13n, privileged: true, channelTypes: [ "T", "V", "S" ] },
    { key: "EMBED_LINKS", bit: 1n << 14n, channelTypes: [ "T", "V", "S" ] },
    { key: "ATTACH_FILES", bit: 1n << 15n, channelTypes: [ "T", "V", "S" ] },
    { key: "READ_MESSAGE_HISTORY", bit: 1n << 16n, channelTypes: [ "T", "V", "S" ] },
    { key: "MENTION_EVERYONE", bit: 1n << 17n, channelTypes: [ "T", "V", "S" ] },
    { key: "USE_EXTERNAL_EMOJIS", bit: 1n << 18n, channelTypes: [ "T", "V", "S" ] },
    { key: "VIEW_GUILD_INSIGHTS", bit: 1n << 19n },
    { key: "CONNECT", bit: 1n << 20n, channelTypes: [ "V", "S" ] },
    { key: "SPEAK", bit: 1n << 21n, channelTypes: [ "V" ] },
    { key: "MUTE_MEMBERS", bit: 1n << 22n, channelTypes: [ "V", "S" ] },
    { key: "DEAFEN_MEMBERS", bit: 1n << 23n, channelTypes: [ "V" ] },
    { key: "MOVE_MEMBERS", bit: 1n << 24n, channelTypes: [ "V", "S" ] },
    { key: "USE_VAD", bit: 1n << 25n, channelTypes: [ "V" ] },
    { key: "CHANGE_NICKNAME", bit: 1n << 26n },
    { key: "MANAGE_NICKNAMES", bit: 1n << 27n },
    { key: "MANAGE_ROLES", bit: 1n << 28n, privileged: true, channelTypes: [ "T", "V", "S" ] },
    { key: "MANAGE_WEBHOOKS", bit: 1n << 29n, privileged: true, channelTypes: [ "T", "V", "S" ] },
    { key: "MANAGE_GUILD_EXPRESSIONS", bit: 1n << 30n, privileged: true },
    { key: "USE_APPLICATION_COMMANDS", bit: 1n << 31n, channelTypes: [ "T", "V", "S" ] },
    { key: "REQUEST_TO_SPEAK", bit: 1n << 32n, channelTypes: [ "S" ] },
    { key: "MANAGE_EVENTS", bit: 1n << 33n, channelTypes: [ "V", "S" ] },
    { key: "MANAGE_THREADS", bit: 1n << 34n, privileged: true, channelTypes: [ "T" ] },
    { key: "CREATE_PUBLIC_THREADS", bit: 1n << 35n, channelTypes: [ "T" ] },
    { key: "CREATE_PRIVATE_THREADS", bit: 1n << 36n, channelTypes: [ "T" ] },
    { key: "USE_EXTERNAL_STICKERS", bit: 1n << 37n, channelTypes: [ "T", "V", "S" ] },
    { key: "SEND_MESSAGES_IN_THREADS", bit: 1n << 38n, channelTypes: [ "T" ] },
    { key: "USE_EMBEDDED_ACTIVITIES", bit: 1n << 39n, channelTypes: [ "T", "V" ] },
    { key: "MODERATE_MEMBERS", bit: 1n << 40n, privileged: true },
    { key: "VIEW_CREATOR_MONETIZATION_ANALYTICS", bit: 1n << 41n, privileged: true },
    { key: "USE_SOUNDBOARD", bit: 1n << 42n, channelTypes: [ "V" ] },
    { key: "CREATE_GUILD_EXPRESSIONS", bit: 1n << 43n },
    { key: "CREATE_EVENTS", bit: 1n << 44n, channelTypes: [ "V", "S" ] },
    { key: "USE_EXTERNAL_SOUNDS", bit: 1n << 45n, channelTypes: [ "V" ] },
    { key: "SEND_VOICE_MESSAGES", bit: 1n << 46n, channelTypes: [ "T", "V", "S" ] },
    { key: "SET_VOICE_CHANNEL_STATUS", bit: 1n << 48n, channelTypes: [ "V" ] },
    { key: "SEND_POLLS", bit: 1n << 49n, channelTypes: [ "T", "V", "S" ] },
    { key: "USE_EXTERNAL_APPS", bit: 1n << 50n, channelTypes: [ "T", "V", "S" ] },
    { key: "PIN_MESSAGES", bit: 1n << 51n, channelTypes: [ "T" ] },
    { key: "BYPASS_SLOWMODE", bit: 1n << 52n, channelTypes: [ "T", "V", "S" ] },
] as const satisfies readonly DiscordPermissionFlag[];

export function parsePermissionBits(value: unknown): bigint | null {
    if (typeof value === "bigint")
        return value;

    if (typeof value === "number" && Number.isFinite(value) && value >= 0)
        return BigInt(Math.trunc(value));

    if (typeof value !== "string")
        return null;

    const trimmed = value.trim();

    if (!trimmed)
        return null;

    if (/^0x[0-9a-f]+$/i.test(trimmed))
        return BigInt(trimmed);

    if (/^[0-9]+$/.test(trimmed))
        return BigInt(trimmed);

    return null;
}

export function diffPermissionBits(oldValue: unknown, newValue: unknown): PermissionDiffEntry[] {
    const oldBits = parsePermissionBits(oldValue);
    const newBits = parsePermissionBits(newValue);

    if (oldBits === null || newBits === null || oldBits === newBits)
        return [];

    return DiscordPermissionFlags.flatMap((permission) => {
        const wasEnabled = (oldBits & permission.bit) !== 0n;
        const isEnabled = (newBits & permission.bit) !== 0n;

        if (wasEnabled === isEnabled)
            return [];

        return [{
            ...permission,
            state: isEnabled ? "added" : "removed",
        } satisfies PermissionDiffEntry];
    });
}
